"use client";

import { TextLoop } from "@/components/ui/text-loop";
import { getCaddieModeConfig, getCaddieMoodConfig, type CaddieMood } from "@/lib/caddie";
import { cn } from "@/lib/utils";
import type { AgentMode } from "@/types/aiChat";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useMemo, useState } from "react";
interface CaddieAvatarProps {
  mode?: AgentMode | null;
  mood?: CaddieMood;
  loopModes?: AgentMode[];
  size?: number;
  decorated?: boolean;
  className?: string;
  imageClassName?: string;
  labelClassName?: string;
  showLabel?: boolean;
  interval?: number;
}
export function CaddieAvatar({
  mode,
  mood,
  loopModes,
  size = 32,
  decorated = false,
  className,
  imageClassName,
  labelClassName,
  showLabel = false,
  interval = 2.8
}: CaddieAvatarProps) {
  const modes = useMemo(() => {
    if (loopModes?.length) {
      return loopModes;
    }
    if (mode) {
      return [mode];
    }
    return [];
  }, [loopModes, mode]);
  const [loopIndex, setLoopIndex] = useState(0);
  const activeConfig = useMemo(() => {
    if (modes.length > 0) {
      return getCaddieModeConfig(modes[loopIndex] ?? modes[0]);
    }
    return getCaddieMoodConfig(mood ?? "default");
  }, [modes, loopIndex, mood]);
  return <div className={cn("inline-flex items-center gap-2", className)}>
      <div className={cn("relative", decorated && "overflow-hidden rounded-full")} style={{
      height: size,
      width: size
    }}>
        {decorated && <motion.div className="shadow-ds-brand-glow-soft absolute inset-0 bg-[radial-gradient(circle_at_top,#eff6ff,transparent_70%),linear-gradient(135deg,#dbeafe,#e4e4e7)] dark:bg-[radial-gradient(circle_at_top,#1d4ed8,transparent_70%),linear-gradient(135deg,#18181b,#27272a)]" />}
        {decorated && <motion.div className="absolute inset-[6%] rounded-full bg-white/80 blur-md dark:bg-blue-400/20" animate={{
        opacity: [0.55, 0.9, 0.55],
        scale: [0.94, 1.02, 0.94]
      }} transition={{
        duration: 3.6,
        repeat: Number.POSITIVE_INFINITY,
        ease: "easeInOut"
      }} />}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={activeConfig.asset} className="absolute inset-0" initial={{
          opacity: 0,
          scale: 0.88,
          rotate: -8
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} exit={{
          opacity: 0,
          scale: 1.08,
          rotate: 8
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }}>
            <Image src={activeConfig.asset} alt={activeConfig.label} fill sizes={`${size}px`} className={cn(activeConfig.imageClassName, imageClassName)} />
          </motion.div>
        </AnimatePresence>
      </div>

      {showLabel && (modes.length > 1 ? <TextLoop interval={interval} onIndexChange={setLoopIndex} className={cn("text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-300", labelClassName)}>
            {modes.map(currentMode => <span key={currentMode}>{getCaddieModeConfig(currentMode).label}</span>)}
          </TextLoop> : <span className={cn("text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-300", labelClassName)}>
            {activeConfig.label}
          </span>)}
    </div>;
}